import { Messages } from '../../codegen/Localize';
import {
  ActionHelpers,
  standardActionErrorMsg,
  standardActionSuccessMsg,
  StandardActionToastMessages
} from '../../commonInfra/contentActions/ActionHelpers';
import { IErrorItem } from '../../commonInfra/contentActions/MultipleItemResult';
import { ISchedule } from '../types/ISchedule';
import { IActionContext } from '../../ts/helpers/ActionContext';
import { L10n } from '../../ts/L10n/L10n';
import { Set } from '../../ts/models/Set';
import { SchedulesService } from '../../ts/resources/Schedules';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { ScheduleErrorsService } from '../../ts/services/ScheduleErrors';
import { openReactConfirmationDialog } from '~src/commonInfra/dialogs/ReactConfirmationDialog';


export namespace SetScheduleStatusActionService {

  export function enableSchedule(schedule: ISchedule): Promise<IResult> {
    return enableSchedules(singleton(schedule));
  }

  export function disableSchedule(schedule: ISchedule): Promise<IResult> {
    return disableSchedules(singleton(schedule));
  }

  function singleton(schedule: ISchedule): Set<ISchedule> {
    return Set.singleton(schedule, (s: ISchedule) => s.id);
  }

  export function confirmEnableSchedules(schedules: Set<ISchedule>, context: IActionContext): Promise<IResult> {
    const dialogText = schedules.size() === 1
      ? Messages.enableScheduleDialogPrompt({ NAME: schedules.getByIndex(0).name })
      : Messages.enableSchedulesDialogPrompt({ ITEM_COUNT_LOC: L10n.localizedCount(schedules.size()) });
    const actionName = schedules.size() === 1
      ? Messages.enableScheduleDialogSubmit()
      : Messages.enableSchedulesDialogSubmit({ ITEM_COUNT_LOC: L10n.localizedCount(schedules.size()) });
    return confirmAction(
      context,
      () => enableSchedules(schedules),
      Messages.enableSchedulesDialogTitle(),
      dialogText,
      actionName,
      );
  }

  export function confirmDisableSchedules(schedules: Set<ISchedule>, context: IActionContext): Promise<IResult> {
    const dialogText = schedules.size() === 1
      ? Messages.disableScheduleDialogPrompt({ NAME: schedules.getByIndex(0).name })
      : Messages.disableSchedulesDialogPrompt({ ITEM_COUNT_LOC: L10n.localizedCount(schedules.size()) });
    const actionName = schedules.size() === 1
      ? Messages.disableScheduleDialogSubmit()
      : Messages.disableSchedulesDialogSubmit({ ITEM_COUNT_LOC: L10n.localizedCount(schedules.size()) });
    return confirmAction(
      context,
      () => disableSchedules(schedules),
      Messages.disableSchedulesDialogTitle(),
      dialogText,
      actionName,
    );
  }

  function confirmAction(
      context: IActionContext,
      action: () => Promise<IResult>,
      title: string,
      bodyText: string,
      actionName: string,
    ) {

    const confirmStatusDialog = openReactConfirmationDialog({
      title: title,
      paramsForAction: undefined,
      message: bodyText,
      actionName: actionName,
      action: action,
      dialogWidth: 400
    });
    return Promise.resolve(confirmStatusDialog.result);

  }

  function enableSchedules(schedules: Set<ISchedule>): Promise<IResult> {
    const translations: StandardActionToastMessages = {
      error: standardActionErrorMsg(Messages.enableSchedulesError),
      success: standardActionSuccessMsg(Messages.enableSchedulesSuccess),
    };

    return SchedulesService.enable(schedules.keys())
      .then((result: IResult) => {
        notifyResult(schedules, result, translations);
        return result;
      });
  }

  function disableSchedules(schedules: Set<ISchedule>): Promise<IResult> {
    const translations: StandardActionToastMessages = {
      error: standardActionErrorMsg(Messages.disableSchedulesError),
      success: standardActionSuccessMsg(Messages.disableSchedulesSuccess),
    };

    return SchedulesService.disable(schedules.keys())
      .then((result: IResult) => {
        notifyResult(schedules, result, translations);
        return result;
      });
  }

  function notifyResult(schedules: Set<ISchedule>, result: IResult, translations: StandardActionToastMessages) {
    ActionHelpers.displayStandardToastsForResult(
      schedules,
      result,
      translations,
      (errorItem: IErrorItem<ISchedule>) => ScheduleErrorsService.errorDetailsFor(errorItem));
  }
}


export type SetScheduleStatusActionService = typeof SetScheduleStatusActionService;
